import { 
  Wand2, 
  Info,
  Terminal,
  ShieldCheck,
  Zap
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

export function AiArchitectTab({ config, onChange }: { config: any, onChange: (val: any) => void }) {
  const enabled = config?.enabled ?? false;

  const toggle = (val: boolean) => {
    onChange({ ...config, enabled: val });
    // Removed toast to avoid auto-save confusion.
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Wand2 className="h-6 w-6 text-primary" />
          AI Server Architect
        </h2>
        <p className="text-muted-foreground">
          Activate the AI engine to build your server structure instantly. 
          <span className="text-primary font-medium ml-1 italic">Make sure to click the Save button above after toggling.</span>
        </p>
      </div>

      <Card className="p-6 border-primary/20 bg-primary/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5">
           <Zap className="h-24 w-24" />
        </div>
        <div className="flex items-center justify-between relative z-10">
          <div className="space-y-1">
            <div className="font-bold flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              Module Activation
            </div>
            <div className="text-sm text-muted-foreground max-w-md">
              Allow server administrators to use AI commands in Discord. 
              This module requires **Administrator** permissions for the bot.
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
             <Switch checked={enabled} onCheckedChange={toggle} />
             <span className="text-[10px] font-bold uppercase tracking-tighter text-muted-foreground">
                {enabled ? "Active" : "Inactive"}
             </span>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5 border-primary/10 bg-card/50 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Terminal className="h-4 w-4" /> Available Commands
          </h3>
          <div className="space-y-3">
             <div className="flex items-start gap-3">
                <Badge variant="secondary" className="font-mono text-xs">/ai-architect</Badge>
                <div className="text-xs text-muted-foreground">
                   Starts the interactive AI builder. Describe your server theme in the prompt.
                </div>
             </div>
             <div className="flex items-start gap-3">
                <Badge variant="secondary" className="font-mono text-xs">/create-template</Badge>
                <div className="text-xs text-muted-foreground">
                   Quickly generate a blueprint for your server.
                </div>
             </div>
          </div>
        </Card>

        <Card className="p-5 border-primary/10 bg-card/50 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Info className="h-4 w-4" /> How it works
          </h3>
          <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
             <p>
               1. Run <span className="text-primary">/ai-architect</span> in your Discord server.
             </p>
             <p>
               2. Provide a prompt (e.g. "Futuristic RP server with staff rooms").
             </p>
             <p>
               3. Review the **Blueprint** generated by AI in Discord.
             </p>
             <p>
               4. Click **[🚀 Deploy]** to let Orbit create all channels and roles automatically.
             </p>
             <div className="flex items-center gap-2 pt-2 text-primary font-bold">
                <ShieldCheck className="h-3.5 w-3.5" />
                Requires Administrator Permissions
             </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
